import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { convert, formatQuantityNumber as fmtQty } from '../utils/unitDisplay';

// Spec C2 / C3 / C4 flag vocabulary → the badge text the reviewer sees.
const FLAG_LABEL = {
  NOT_COUNTED: 'Not counted', WRONG_FAMILY: 'Wrong family', UNKNOWN_UNIT: 'Unknown unit',
  INVALID_UNIT: 'Invalid unit', AMBIGUOUS: 'Ambiguous', CANNOT_READ: 'Unreadable',
  PRICE_CONFLICT: 'Price conflict', CHECK_SIZE: 'Check size'
};

// The interpretation chain (spec C5): raw ink → the unit it was written in →
// the item's own unit. Shown on clean rows too, so the owner verifies the
// MEANING, not just the digits.
const chainText = (item) => {
  const q = Number(item.edited_quantity);
  // Kitchen workbook: the total of several tabs — say where each part was counted.
  const parts = Array.isArray(item.location_breakdown) ? item.location_breakdown : [];
  if (parts.length > 1) {
    const sum = parts.reduce((s, p) => s + Number(p.qty || 0), 0);
    const line = `${parts.map(p => `${p.name} ${fmtQty(p.qty)}`).join(' + ')} = ${fmtQty(sum)} ${item.unit}`;
    return Math.abs(sum - q) < 1e-9 ? line : `${line} → you entered ${fmtQty(q)} ${item.unit}`;
  }
  const written = item.resolved_unit && String(item.resolved_unit).toLowerCase() !== String(item.unit || '').toLowerCase()
    ? item.resolved_unit : null;
  const inWritten = written ? convert(q, item.unit, written) : null;
  const mid = inWritten === null ? '' : `${fmtQty(inWritten)} ${written} → `;
  return `wrote “${item.value_raw}” → ${mid}${fmtQty(q)} ${item.unit}`;
};

/**
 * ClosingStockOCRReview
 *
 * Review pass over a scanned count sheet before its values land on the sheet.
 *
 * Two different rules meet here, and they are easy to confuse:
 *
 *  • WITHIN one scan — the same item written twice with DIFFERENT numbers is a
 *    disagreement about one measurement, not two batches of stock. The backend
 *    sends those as `conflict` rows carrying no value, and the count cannot be
 *    applied until each is settled. Never added together.
 *
 *  • ACROSS scans — a scan ADDS to whatever the sheet already holds, because
 *    "Add More" means another batch of stock was counted (the walk-in, then the
 *    dry store). `existingQtyOf` supplies the current figure so each row can
 *    show the running total before anything is applied.
 */
const ClosingStockOCRReview =({ isOpen, onClose, ocrResult, onConfirm, imagePreviewUrl, imagePreviewUrls = [], existingQtyOf = null }) => {
  const [editedItems, setEditedItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [filterMode, setFilterMode] = useState('scanned');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [expandedReasons, setExpandedReasons] = useState({});

  const toggleReason = useCallback((rowKey) => {
    setExpandedReasons(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
  }, []);

  const imagePreviews = imagePreviewUrls && imagePreviewUrls.length > 0 ? imagePreviewUrls : (imagePreviewUrl ? [imagePreviewUrl] : []);
  const hasMultipleImages = imagePreviews.length > 1;
  const currentImageUrl = imagePreviews[currentImageIndex] || imagePreviewUrl;

  // The row code (R5698 / P224 / C86) is unique per item and already encodes the
  // item_type, so it is the natural key — the three id spaces overlap. A POS
  // import row has no code and no id until it is picked, so it is stamped with
  // a stable `_rowKey` at load time that survives the pick.
  const rowKeyOf = (it) => it._rowKey || it.code || `${it.item_type || 'raw'}:${it.item_id}`;

  // Every item in the business, for the picker's "search all" fallback.
  const allItems = useMemo(() => ocrResult?.all_items || [], [ocrResult]);
  const notImported = ocrResult?.not_imported || [];

  useEffect(() => {
    setItemToRemove(null);
    setExpandedReasons({});
    if (ocrResult?.items?.length > 0) {
      // Keep the order the items were read off the sheet (scan_index). Items
      // that weren't scanned have no position, so they sit after the scanned
      // ones. Conflicts are NOT floated to the top — order is preserved and they
      // stay findable via the "Needs a decision" filter and their red badge.
      const pos = (x) => (x.scan_index == null ? Number.MAX_SAFE_INTEGER : x.scan_index);
      const sorted = [...ocrResult.items].sort((a, b) => pos(a) - pos(b));

      setEditedItems(sorted.map((item, idx) => ({
        ...item,
        _rowKey: item.code
          || (item.item_id != null ? `${item.item_type || 'raw'}:${item.item_id}` : `src:${item.source_name || idx}`),
        edited_quantity: item.scanned_quantity !== null && item.scanned_quantity !== undefined
          ? String(item.scanned_quantity) : '',
        // Price: sheet price → (open price decision keeps the box empty so the
        // chips stay visible) → vendor price fallback. Editable either way.
        edited_price: item.sheet_price !== null && item.sheet_price !== undefined
          ? String(item.sheet_price)
          : (item.price_candidates?.length > 0 ? ''
            : (Number(item.latest_vendor_price) > 0 ? String(item.latest_vendor_price) : '')),
        was_edited: false
      })));
      setFilterMode('scanned');
    } else {
      setEditedItems([]);
    }
  }, [ocrResult]);

  const handleQuantityChange = useCallback((rowKey, value, label = null) => {
    setEditedItems(prev => {
      const currentItem = prev.find(it => rowKeyOf(it) === rowKey);
      if (value === '' && currentItem && currentItem.edited_quantity !== '' && currentItem.edited_quantity !== null) {
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }
        setItemToRemove({
          item: currentItem,
          rowKey,
          previousValue: currentItem.edited_quantity
        });
        return prev;
      }

      return prev.map(it => (rowKeyOf(it) !== rowKey ? it : {
        ...it,
        edited_quantity: value,
        was_edited: true,
        // A chip pick is a decision worth remembering (spec C6-6): which reading won.
        _resolution: (value !== '' && label) ? { ...(it._resolution || {}), quantity: label } : it._resolution,
        // Typing or picking a value settles any open decision — the operator
        // has decided (a conflict, a unit pick, or a candidate choice alike).
        conflict: value !== '' ? false : it.conflict,
        needs_unit_pick: value !== '' ? false : it.needs_unit_pick,
        confidence: value ? 'high' : it.confidence
      }));
    });
  }, []);

  // POS import: resolve a name-only row onto a real item. The quantity the
  // report gave is in the REPORT's unit, so it is deliberately NOT carried
  // over — the operator enters the count in the item's own unit, which is the
  // whole reason the row was held back.
  const handleItemPick = useCallback((rowKey, rawItemId) => {
    const itemId = Number(rawItemId);
    if (!Number.isFinite(itemId)) return;
    setEditedItems(prev => prev.map(it => {
      if (rowKeyOf(it) !== rowKey) return it;
      const picked = (it.item_candidates || []).find(c => Number(c.item_id) === itemId)
        || (allItems || []).find(a => Number(a.item_id) === itemId);
      if (!picked) return it;
      return {
        ...it,
        item_id: picked.item_id,
        item_type: picked.item_type || it.item_type,
        item_name: picked.item_name,
        unit: picked.unit || it.unit,
        code: picked.code || it.code,
        picked_item_id: picked.item_id,
        needs_item_pick: false,
        was_edited: true,
        confidence: 'high',
        confidence_reason: ''
        // _rowKey is deliberately NOT touched — the row must keep its identity
        // across the pick or React would remount it and lose the edit.
      };
    }));
  }, [allItems]);

  // Prices are flags-only: editing one never gates Confirm, so this is a
  // simpler sibling of handleQuantityChange (no removal confirmation).
  const handlePriceChange = useCallback((rowKey, value, label = null) => {
    setEditedItems(prev => prev.map(it => (rowKeyOf(it) !== rowKey ? it : {
      ...it,
      edited_price: value,
      was_edited: true,
      _resolution: (value !== '' && label) ? { ...(it._resolution || {}), price: label } : it._resolution
    })));
  }, []);

  const confirmRemoveItem = useCallback(() => {
    if (!itemToRemove) return;
    const targetKey = itemToRemove.rowKey;
    setEditedItems(prev => prev.map(it => (rowKeyOf(it) !== targetKey ? it : {
      ...it,
      edited_quantity: '',
      was_edited: true,
      conflict: false,
    })));
    setItemToRemove(null);
  }, [itemToRemove]);

  const cancelRemoveItem = useCallback(() => {
    setItemToRemove(null);
  }, []);

  // Everything that demands a human decision before the count can be applied:
  // multi-row conflicts, cross-family unit picks, and open parse/shift
  // candidates. Nil markers deliberately do NOT block — a sheet full of dashes
  // must not demand thirty forced taps; unconfirmed nils simply stay uncounted.
  const unresolvedConflicts = useMemo(
    () => editedItems.filter(i =>
      (i.conflict || i.needs_unit_pick || (i.candidates?.length > 1))
      && (i.edited_quantity === '' || i.edited_quantity === null)),
    [editedItems]
  );

  const unconfirmedNils = useMemo(
    () => editedItems.filter(i => i.marked_nil && (i.edited_quantity === '' || i.edited_quantity === null)),
    [editedItems]
  );

  const handleConfirm = useCallback(async () => {
    if (unresolvedConflicts.length > 0) return;
    setSubmitting(true);
    try {
      const finalItems = editedItems
        .filter(item => item.edited_quantity !== '' && item.edited_quantity !== null)
        .map(item => ({
          item_id: item.item_id,
          item_type: item.item_type,
          item_name: item.item_name,
          unit: item.unit,
          quantity: Number(item.edited_quantity),
          confidence: item.confidence,
          // POS import: what the report called it, and the item a human chose
          // for it — the pair the mapping memory learns from.
          ...(item.source_name ? { source_name: item.source_name } : {}),
          ...(item.picked_item_id ? { picked_item_id: item.picked_item_id } : {}),
          // ₹ per display unit; null = no price decided (never zero — a zero
          // would poison the batch-fallback pricing downstream).
          unit_price: item.edited_price !== '' && item.edited_price !== null
            && item.edited_price !== undefined && Number.isFinite(Number(item.edited_price))
            ? Number(item.edited_price) : null,
          // Spec D7 — raw + derived travel with the count: the verbatim ink, the
          // unit it was written in, the paid/pack pair, every flag and its resolution.
          audit: {
            value_raw: item.value_raw ?? null,
            unit_written: item.resolved_unit ?? null,
            paid_raw: item.paid_raw ?? null,
            pack_qty: item.pack_qty ?? null,
            pack_unit: item.pack_unit ?? null,
            flags: Array.isArray(item.flags) ? item.flags : [],
            flag_resolutions: item._resolution || {}
          }
        }));

      if (finalItems.length === 0) {
        alert('No items with quantities to submit. Please enter at least one value.');
        setSubmitting(false);
        return;
      }

      await onConfirm(finalItems);
    } catch (error) {
      alert('Failed to save: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  }, [editedItems, onConfirm, unresolvedConflicts]);

  if (!isOpen) return null;

  const meta = ocrResult?.metadata || {};
  const unresolved = ocrResult?.unresolved || [];
  const twinRows = ocrResult?.twin_rows || [];
  // Anything still awaiting a decision, nil markers included (the filter shows
  // them; only conflicts / unit picks / open candidates actually BLOCK
  // confirm). Once a value lands the decision is made and the row drops out.
  // `needs_item_pick` is included so a POS yellow stays VISIBLE in the default
  // filter, but it is deliberately absent from `unresolvedConflicts` — an
  // unpicked row simply imports nothing; it must never block the whole count.
  const needsDecision = (i) =>
    (i.conflict || i.needs_unit_pick || (i.candidates?.length > 1) || i.marked_nil || i.needs_item_pick)
    && (i.edited_quantity === '' || i.edited_quantity === null);
  const conflictCount = editedItems.filter(needsDecision).length;
  const filledCount = editedItems.filter(i => i.edited_quantity !== '' && i.edited_quantity !== null).length;
  const totalCount = editedItems.length;

  const addsToCount = existingQtyOf
    ? editedItems.filter(i => Number(i.edited_quantity) > 0 && existingQtyOf(i) > 0).length
    : 0;
  const filteredItems =
    filterMode === 'all' ? editedItems
    : filterMode === 'conflict' ? editedItems.filter(needsDecision)
    // 'scanned' — what the sheet actually gave us, plus anything awaiting a
    // decision. An imported row (source_name) stays visible even once its item
    // is picked: the operator still has to type the count in the item's unit,
    // and a row that vanished the moment you chose it would be baffling.
    : editedItems.filter(i => i.edited_quantity !== '' || needsDecision(i) || !!i.source_name);

  const getConfidenceClass = (item) => {
    if (item.conflict || item.needs_unit_pick || item.confidence === 'low') return 'ocr-confidence-low';
    if (item.confidence === 'medium') return 'ocr-confidence-medium';
    return 'ocr-confidence-high';
  };

  const getConfidenceBadge = (item) => {
    if (item.conflict || item.needs_unit_pick) return 'Pick';
    if (item.confidence === 'low') return 'Low';
    if (item.confidence === 'medium') return 'Med';
    return 'High';
  };

  return (
    <div className="ocr-modal-overlay">
      <div className="ocr-modal">

        {/* Header */}
        <div className="ocr-modal-header">
          <div className="ocr-modal-header-left">
            <h2 className="ocr-modal-title">
              {ocrResult?.metadata?.source === 'sheet_import' ? 'Imported Sheet Review' : 'OCR Scan Review'}
            </h2>
          </div>
          <button className="ocr-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Thumbnails */}
        {imagePreviews.length > 0 && (
          <div className="ocr-top-row">
            <div className="ocr-thumbs-container">
              {imagePreviews.map((url, idx) => (
                <div key={idx} className={`ocr-thumb-wrap ${idx === currentImageIndex ? 'active' : ''}`}>
                  <img
                    src={url}
                    alt={`Scanned page ${idx + 1}`}
                    className="ocr-thumb-img"
                    onClick={() => { setCurrentImageIndex(idx); setShowLightbox(true); }}
                    title={`Click to expand page ${idx + 1}`}
                  />
                  {imagePreviews.length > 1 && (<span className="ocr-thumb-badge">{idx + 1}</span>)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Values the scan could not attach to a row on THIS sheet */}
        {unresolved.length > 0 && (
          <div className="ocr-unresolved-note">
            &#9888; {unresolved.length} value{unresolved.length > 1 ? 's' : ''} unmatched &amp; left out — verify this is the correct sheet for this business.
          </div>

        )}

        {/* Counts written on a saved preparation's placeholder twin (an older
            printed sheet still carries its row): never added — the preparation
            row is where that stock is counted. */}
        {twinRows.length > 0 && (
          <div className="ocr-unresolved-note ocr-twin-note" data-testid="ocr-twin-note">
            &#9888; Written on a saved preparation&rsquo;s stock row &mdash; not added. Count these on the preparation row instead:
            <ul>
              {twinRows.map(t => (
                <li key={t.code}>
                  {t.item_name} ({(t.written || []).join(' + ')} written) &rarr; &ldquo;{t.prep_name}&rdquo; preparation row
                </li>
              ))}
            </ul>
          </div>
        )}

        {ocrResult?.fingerprint_warning && (
          <div className="ocr-fingerprint-warning">
            &#9888; {ocrResult.fingerprint_warning}
          </div>
        )}

        {notImported.length > 0 && (
          <details className="ocr-not-imported">
            <summary>Not imported ({notImported.length})</summary>
            <ul className="ocr-not-imported-list">
              {notImported.map((r, i) => (
                <li key={i}>
                  <strong>{r.source_name}</strong>
                  {r.qty ? ` · ${r.qty} ${r.unit || ''}` : ''}
                  {r.reason ? ` — ${r.reason}` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}

        {unconfirmedNils.length > 0 && (
          <div className="ocr-nil-note">
            {unconfirmedNils.length} row{unconfirmedNils.length > 1 ? 's are' : ' is'} marked nil on the sheet
            &mdash; tap their &ldquo;Confirm 0&rdquo; to count them as zero, or leave them uncounted.
          </div>
        )}

        {addsToCount > 0 && (
          <div className="ocr-adds-note">
            {addsToCount} of these {addsToCount === 1 ? 'is' : 'are'} already counted on the sheet &mdash;
            this scan <strong>adds to</strong> them rather than replacing them.
          </div>
        )}

        {/* Filters */}
        <div className="ocr-filter-row">
          <button type="button" className={`ocr-filter-btn ${filterMode === 'scanned' ? 'active' : ''}`} onClick={() => setFilterMode('scanned')}>
            Scanned ({filledCount + conflictCount})
          </button>
          {conflictCount > 0 && (
            <button type="button" className={`ocr-filter-btn ocr-filter-conflict ${filterMode === 'conflict' ? 'active' : ''}`} onClick={() => setFilterMode('conflict')}>
              Needs a decision ({conflictCount})
            </button>
          )}
          <button type="button" className={`ocr-filter-btn ${filterMode === 'all' ? 'active' : ''}`} onClick={() => setFilterMode('all')}>
            All items ({totalCount})
          </button>
        </div>

        {/* Grid */}
        <div className="ocr-grid-panel">
          <div className="ocr-grid-header">
            <div className="ocr-grid-col ocr-col-name">Item Name</div>
            <div className="ocr-grid-col ocr-col-qty">Qty</div>
            <div className="ocr-grid-col ocr-col-unit">Unit</div>
            <div className="ocr-grid-col ocr-col-price">Price</div>
          </div>

          <div className="ocr-grid-body">
            {filteredItems.map((item, index) => {
              const key = rowKeyOf(item);
              // A scan ADDS to the sheet, so show the running total up front.
              const already = existingQtyOf ? existingQtyOf(item) : 0;
              return (
                <div key={key} className={`ocr-grid-row ${getConfidenceClass(item)}`}>
                  <div className="ocr-grid-col ocr-col-name">
                    <div className="ocr-item-title-row">
                      <span className="ocr-item-number">{index + 1}.</span>
                      <span className="ocr-item-name">{item.item_name}</span>
                      {Array.isArray(item.flags) && item.flags.map(f => (
                        <span key={f} className={`ocr-flag-badge ocr-flag-${String(f).toLowerCase()}`} title={f}>{FLAG_LABEL[f] || f}</span>
                      ))}
                      {/* POS import: the report gave a NAME, not an identity.
                          Until one is picked the row imports nothing. */}
                      {item.needs_item_pick && (
                        <select
                          className="ocr-item-picker"
                          aria-label={`Choose the item for ${item.source_name || item.item_name}`}
                          value=""
                          onChange={(e) => handleItemPick(key, e.target.value)}
                        >
                          <option value="">Pick the item…</option>
                          {(item.item_candidates || []).map(c => (
                            <option key={c.item_id} value={c.item_id}>
                              {c.item_name} · {Math.round((c.score || 0) * 100)}%
                            </option>
                          ))}
                          {allItems.length > 0 && <option disabled>──────────</option>}
                          {allItems
                            .filter(a => !(item.item_candidates || []).some(c => c.item_id === a.item_id))
                            .map(a => (
                              <option key={`all-${a.item_type}-${a.item_id}`} value={a.item_id}>
                                {a.item_name}
                              </option>
                            ))}
                        </select>
                      )}
                      {!item.conflict && item.confidence_reason && item.confidence !== 'high' && (
                        <button
                          type="button"
                          className="ocr-reason-toggle-btn"
                          onClick={() => toggleReason(key)}
                          title="Click to view explanation"
                        >
                          <svg
                            className={`ocr-chevron-icon ${expandedReasons[key] ? 'expanded' : ''}`}
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </button>
                      )}
                    </div>
                    {/* One decision strip for every open choice: multi-row
                        conflicts, parse/shift candidates, and nil markers.
                        Candidates win over raw readings — they carry labels
                        (and the nil-vs-number 0 chip) from the backend. */}
                    {(() => {
                      if (item.edited_quantity !== '' && item.edited_quantity !== null) return null;
                      // Kitchen workbook: a tab that could not be read holds the
                      // whole item. No one-tap pick — the readable tabs' number
                      // is a partial total. Say which tab, and ask for the total.
                      if (item.conflict && Array.isArray(item.location_breakdown)) {
                        return <span className="ocr-item-conflict">{item.confidence_reason}</span>;
                      }
                      const chips = (item.candidates?.length > 0)
                        ? item.candidates.map(c => ({ qty: c.qty, label: c.label || `${c.qty} ${item.unit}`, page: null }))
                        : (item.conflict
                          ? (item.readings || [])
                              .filter(r => r.qty !== null && r.qty !== undefined)
                              .map(r => ({ qty: r.qty, label: `${r.qty} ${item.unit}`, page: r.page }))
                          : []);
                      if (chips.length === 0 && !item.marked_nil) return null;
                      return (
                        <span className="ocr-item-conflict">
                          {item.conflict
                            ? <>Counted {chips.length} times &mdash; pick one:{' '}</>
                            : (item.marked_nil && chips.length === 0)
                              ? <>Marked nil on the sheet &mdash;{' '}</>
                              : <>Pick one:{' '}</>}
                          {chips.map((c, i) => (
                            <span key={i} className="ocr-conflict-choice">
                              <button
                                type="button"
                                className="ocr-conflict-pick"
                                onClick={() => handleQuantityChange(key, String(c.qty), c.label)}
                                title={c.page ? `Use ${c.qty} ${item.unit} (from page ${c.page})` : `Use ${c.qty} ${item.unit}`}
                              >
                                {c.label}
                              </button>
                            </span>
                          ))}
                          {item.marked_nil && (
                            <button
                              type="button"
                              className="ocr-conflict-pick"
                              onClick={() => handleQuantityChange(key, '0')}
                              title="Count this item as zero"
                            >
                              Confirm 0
                            </button>
                          )}
                        </span>
                      );
                    })()}
                    {/* The interpretation chain — the owner verifies the
                        reading, not just the digits. Hidden when the ink and
                        the number agree ("5" → 5). */}
                    {item.value_raw && item.edited_quantity !== '' && item.edited_quantity !== null && (
                      <span className="ocr-item-chain">{chainText(item)}</span>
                    )}
                    {/* The price chain, chips, and reason — flags only, they
                        never gate Confirm. */}
                    {item.edited_price !== '' && item.edited_price !== null && item.edited_price !== undefined
                      && Number(item.paid_raw) > 0 && Number(item.pack_qty) > 0 ? (
                      <span className="ocr-item-chain">
                        ₹{fmtQty(item.paid_raw)} ÷ {fmtQty(item.pack_qty)} {item.pack_unit || item.unit} = ₹{item.edited_price}/{item.unit}
                      </span>
                    ) : (item.price_raw && item.edited_price !== '' && item.edited_price !== null
                      && String(item.edited_price) !== String(item.price_raw).trim() && (
                      <span className="ocr-item-chain">
                        wrote &ldquo;{item.price_raw}&rdquo; &rarr; &#8377;{item.edited_price}/{item.unit}
                      </span>
                    ))}
                    {item.price_candidates?.length > 0 && (item.edited_price === '' || item.edited_price === null) && (
                      <span className="ocr-item-conflict">
                        {item.price_candidates.map((c, i) => (
                          <button key={i} type="button" className="ocr-conflict-pick"
                            onClick={() => handlePriceChange(key, String(c.price), c.label)}
                            title={`Use ₹${c.price} per ${item.unit}`}>{c.label}</button>
                        ))}
                      </span>
                    )}
                    {item.price_reason && (
                      <span className="ocr-item-reason">{item.price_reason}</span>
                    )}
                    {!item.conflict && item.confidence_reason && item.confidence !== 'high' && expandedReasons[key] && (
                      <span className="ocr-item-reason">{item.confidence_reason}</span>
                    )}
                    {already > 0 && Number(item.edited_quantity) > 0 && (
                      <span className="ocr-item-adds">
                        {already} {item.unit} already counted &mdash; this scan adds {Number(item.edited_quantity)} {item.unit},
                        {' '}making <strong>{Math.round((already + Number(item.edited_quantity)) * 10000) / 10000} {item.unit}</strong>
                      </span>
                    )}
                  </div>
                  <div className="ocr-grid-col ocr-col-qty">
                    <input
                      type="number"
                      aria-label={`Quantity for ${item.item_name}`}
                      className={`ocr-qty-input${
                        item.match_status === 'green' ? ' input-green'
                        : (item.conflict || item.needs_item_pick || item.confidence === 'low') ? ' input-low'
                        : item.confidence === 'medium' ? ' input-medium' : ''}`}
                      value={item.edited_quantity}
                      onChange={(e) => handleQuantityChange(key, e.target.value)}
                      placeholder={item.conflict ? 'Pick' : 'Enter qty'}
                      min="0"
                      step="any"
                      inputMode="decimal"
                    />
                  </div>
                  <div className="ocr-grid-col ocr-col-unit">{item.unit}</div>
                  <div className="ocr-grid-col ocr-col-price">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="any"
                      aria-label={`Price for ${item.item_name}`}
                      className={`ocr-qty-input ocr-price-input${item.price_confidence === 'low' ? ' input-low' : ''}`}
                      value={item.edited_price ?? ''}
                      onChange={(e) => handlePriceChange(key, e.target.value)}
                      placeholder="₹"
                    />
                  </div>
                </div>
              );
            })}
            {filteredItems.length === 0 && (
              <div className="ocr-empty-note">Nothing to show in this view.</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="ocr-modal-footer">
          <div className="ocr-footer-info">
            {unresolvedConflicts.length > 0
              ? <span className="ocr-footer-blocked">{unresolvedConflicts.length} item{unresolvedConflicts.length > 1 ? 's need' : ' needs'} a value chosen</span>
              : <span>{filledCount} of {totalCount} items have values{meta.pages_read ? ` · ${meta.pages_read} page${meta.pages_read > 1 ? 's' : ''} read` : ''}</span>}
          </div>
          <div className="ocr-footer-actions">
            <button className="ocr-btn-cancel" onClick={onClose}>Cancel</button>
            <button
              className="ocr-btn-confirm"
              onClick={handleConfirm}
              disabled={submitting || filledCount === 0 || unresolvedConflicts.length > 0}
              title={unresolvedConflicts.length > 0 ? 'Settle the conflicting counts first' : undefined}
            >
              {submitting ? 'Saving...' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && currentImageUrl && (
        <div className="ocr-lightbox-overlay" onClick={e => e.stopPropagation()}>
          <div className="ocr-lightbox-inner">
            <button className="ocr-lightbox-close" onClick={() => setShowLightbox(false)}>✕</button>
            <img src={currentImageUrl} alt="Scanned sheet full" className="ocr-lightbox-img" />
            {hasMultipleImages && (
              <div className="ocr-lightbox-nav">
                <button
                  onClick={() => setCurrentImageIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentImageIndex === 0}
                  className="ocr-lightbox-nav-btn"
                >◀</button>
                <span>{currentImageIndex + 1} / {imagePreviews.length}</span>
                <button
                  onClick={() => setCurrentImageIndex(prev => Math.min(imagePreviews.length - 1, prev + 1))}
                  disabled={currentImageIndex === imagePreviews.length - 1}
                  className="ocr-lightbox-nav-btn"
                >▶</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Removal Confirmation Popup Modal */}
      {itemToRemove && (
        <div className="ocr-confirm-overlay" onClick={cancelRemoveItem}>
          <div className="ocr-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="ocr-confirm-header">
              <div className="ocr-confirm-icon-wrap">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </div>
              <h3 className="ocr-confirm-title">Remove item completely</h3>
            </div>
            <p className="ocr-confirm-text">
              Are you sure you want to remove <strong>{itemToRemove.item.item_name}</strong> completely?
            </p>
            <div className="ocr-confirm-actions">
              <button type="button" className="ocr-confirm-btn-cancel" onClick={cancelRemoveItem}>
                Cancel
              </button>
              <button type="button" className="ocr-confirm-btn-remove" onClick={confirmRemoveItem}>
                Yes, remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClosingStockOCRReview;
